// 탭 키: 불량내역
// Scope: WRK_REG_POPUP menu-common only.
// Rule:
// - Keep popup-specific logic here.
// - Generic utility candidates can stay here for now, but mark with
//   `@promote-to-shared` to move later in one batch.

import { normalizeUpperKey, toNum } from "@shared/utils/CommonUtils";

export const TAB_REJECT = "1";
// 탭 키: 투입자재
export const TAB_MATERIAL = "2";
// 탭 키: 검사항목
export const TAB_INSPECTION = "3";

// Y/N 플래그를 대소문자/공백 무시 기준으로 판정한다.
// Purpose: normalize any Y/N-like value to a strict Y check.
export const isYnY = (v: any) => normalizeUpperKey(v) === "Y";

// 바코드 비교용 정규화(공백 제거 + 대문자화).
// Purpose: barcode compare key (trim + upper-case).
export const normalizeBarcode = (v: any) => normalizeUpperKey(v);

// grid1 cd_reject 컬럼의 js_combo에서 불량코드(code) 목록 추출
// Purpose: collect valid reject barcode codes from grid column metadata.
export const extractRejectCodeListFromGridColumns = (grid1Columns: any[]) => {
  const cols = Array.isArray(grid1Columns) ? grid1Columns : [];
  const rejectCol = cols.find((col: any) => {
    const key1 = (col?.cd_column || "").toString().toLowerCase();
    const key2 = (col?.field || "").toString().toLowerCase();
    return key1 === "cd_reject" || key2 === "cd_reject";
  });
  if (!rejectCol) return [];

  let rawCombo: any = rejectCol?.js_combo;
  if (typeof rawCombo === "string") {
    try {
      rawCombo = JSON.parse(rawCombo);
    } catch {
      rawCombo = [];
    }
  }

  return (Array.isArray(rawCombo) ? rawCombo : [])
    .map((item: any) => {
      if (typeof item === "string") return item;
      return item?.code ?? item?.cd_flag ?? "";
    })
    .map((v: any) => normalizeBarcode(v))
    .filter((v: string) => !!v);
};

// partslist/prd_issuel 연결용 복합키
// SN/LOT의 경우 바코드(no_sn/no_lot)를 포함해 키 충돌을 방지한다.
// Purpose: stable material key for qty/use accumulation and matching.
export const makePartKey = (row: any) => {
  const noIo = (row?.no_io || "").toString().trim();
  const noLineIo = Number(row?.no_line_io ?? 0) || 0;
  const noLineLotIo = Number(row?.no_line_lot_io ?? 0) || 0;
  const cdMatl = (row?.cd_matl || "").toString().trim();
  const noLineBill = Number(row?.no_line_bill ?? 0) || 0;
  const fgUnit = (row?.fg_unit || "").toString().trim().toUpperCase();
  const noSn = (row?.no_sn || "").toString().trim();
  const noLot = (row?.no_lot || "").toString().trim();
  return `${noIo}|${noLineIo}|${noLineLotIo}|${cdMatl}|${noLineBill}|${fgUnit}|${noSn}|${noLot}`;
};

// FE 기준 가용잔량 = qt_remain - qt_use - (이번 루프 내 임시사용량)
// Purpose: compute remaining usable quantity on client side.
export const getPartAvailableQty = (row: any, extraUse = 0) => {
  const qtRemain = Number(row?.qt_remain ?? 0) || 0;
  const qtUse = Number(row?.qt_use ?? 0) || 0;
  return qtRemain - qtUse - extraUse;
};

// 투입자재 row가 "추가 스캔이 필요한 상태"인지 판정한다.
// 조건: fg_unit이 SN/LOT 이고 no_barcode가 비어 있음.
// Purpose: decide whether a material row still needs barcode input.
export const isMatlRowPending = (row: any) => {
  const fgUnit = (row?.fg_unit || "").toString().trim().toUpperCase();
  if (fgUnit !== "SN" && fgUnit !== "LOT") return false;
  return !(row?.no_barcode || "").toString().trim();
};

// 그리드의 현재 전체 row를 안정적으로 수집한다.
// 우선순위: wrapper.forEachNode -> api.forEachNode -> displayedRows(옵션) -> stateRows -> getChange
// Purpose: read all row data with safe fallbacks by grid lifecycle timing.
export const collectRowsFromGrid = (
  gridRef: any,
  stateRowsSource: any[],
  includeDisplayedRows = false
) => {
  const rows: any[] = [];

  if (gridRef?.forEachNode) {
    gridRef.forEachNode((node: any) => {
      if (node?.data) rows.push(node.data);
    });
  }

  const api = gridRef?.api;
  if (rows.length < 1 && api?.forEachNode) {
    api.forEachNode((node: any) => {
      if (node?.data) rows.push(node.data);
    });
  }

  if (
    includeDisplayedRows &&
    rows.length < 1 &&
    api?.getDisplayedRowCount &&
    api?.getDisplayedRowAtIndex
  ) {
    const cnt = Number(api.getDisplayedRowCount?.() ?? 0) || 0;
    for (let i = 0; i < cnt; i++) {
      const node = api.getDisplayedRowAtIndex(i);
      if (node?.data) rows.push(node.data);
    }
  }

  if (rows.length > 0) return rows;

  const stateRows = Array.isArray(stateRowsSource) ? stateRowsSource : [];
  if (stateRows.length > 0) return stateRows;

  const changes = gridRef?.getChange?.() || {};
  const inserts = Array.isArray(changes.insert) ? changes.insert : [];
  const updates = Array.isArray(changes.update) ? changes.update : [];
  return [...inserts, ...updates];
};

// 그리드 footer의 특정 필드값을 읽는다.
// 공통 패턴: calculateTotals() 후 getFooterData() 순서로 접근한다.
// @promote-to-shared
// Purpose: read one footer field using the standard calculateTotals -> getFooterData sequence.
export const getFooterFieldValue = async (
  gridRefOrApiRef: any,
  field: string
) => {
  const gridRef = gridRefOrApiRef?.current ?? gridRefOrApiRef;
  if (!gridRef || !field) return null;

  await gridRef?.calculateTotals?.();
  const footerData = gridRef?.getFooterData?.();
  if (!footerData) return null;

  return footerData?.[field] ?? null;
};

// [불량실적수량 유효범위]
// 불량내역(유형별 집계)만으로는 실제 불량품 개수를 확정할 수 없다.
// 한 불량품에 불량유형이 여러 개 붙을 수 있어, 유형이 전부 겹치면 하한 / 전부 다른
// 불량품이면 상한이 되고 그 사이 값은 시스템이 알 수 없다(작업자 입력값).
// - min: 유형별 합계의 최댓값. 같은 유형이 여러 항번에 나뉘어 입력될 수 있으므로
//        cd_reject로 합산한 뒤 최댓값을 취한다(행별 최댓값은 하한을 과소평가한다).
// - max: 전체 행 합계.
// 예) RF특성 1 / NMS 1 / RF특성 2 -> 유형별 RF특성 3, NMS 1 -> min 3, max 4
// Purpose: compute the valid [min, max] range of panel qt_b from reject detail rows.
export const getRejectQtyBounds = (grid1Rows: any[]) => {
  // cd_reject 공백 또는 수량 0 행은 prd_runl_re 저장 대상이 아니므로 범위 계산에서도 제외한다.
  const rows = (Array.isArray(grid1Rows) ? grid1Rows : []).filter(
    (row: any) => {
      const cdReject = (row?.cd_reject || "").toString().trim();
      return !!cdReject && toNum(row?.qt_b) > 0;
    }
  );

  const sumByType = new Map<string, number>();
  rows.forEach((row: any) => {
    const key = normalizeBarcode(row?.cd_reject);
    sumByType.set(key, (sumByType.get(key) || 0) + toNum(row?.qt_b));
  });

  let min = 0;
  let minType = "";
  sumByType.forEach((qty: number, key: string) => {
    if (qty > min) {
      min = qty;
      minType = key;
    }
  });

  const max = rows.reduce((sum: number, row: any) => sum + toNum(row?.qt_b), 0);

  return { min, max, minType, rows };
};

// 불량내역 기준으로 패널 불량실적수량을 유효범위 안으로 보정한다.
// 덮어쓰기가 아니라 clamp이므로 작업자가 직접 수정한 값이 범위 내라면 보존된다.
// Purpose: clamp panel qt_b into the valid range instead of overwriting it.
export const clampRejectQty = (currentQtB: any, grid1Rows: any[]) => {
  const { min, max } = getRejectQtyBounds(grid1Rows);
  if (max <= 0) return 0;
  return Math.min(Math.max(toNum(currentQtB), min), max);
};

// [불량실적수량 저장 검증]
// 불량내역과 패널 수량의 정합성을 등식이 아니라 유효범위로 검증한다.
// Purpose: validate panel qty against reject detail; returns an error message or null.
export const findRejectQtyError = (runhData: any, grid1Rows: any[]) => {
  const { min, max, rows } = getRejectQtyBounds(grid1Rows);
  const qtB = toNum(runhData?.qt_b);
  const qtW = toNum(runhData?.qt_w);
  const qt = toNum(runhData?.qt);

  // 불량유형 없이 수량만 입력된 행은 prd_runl_re에서 누락되어 헤더와 상세가 어긋난다.
  const hasQtyWithoutType = (Array.isArray(grid1Rows) ? grid1Rows : []).some(
    (row: any) =>
      toNum(row?.qt_b) > 0 && !(row?.cd_reject || "").toString().trim()
  );
  if (hasQtyWithoutType) return "불량내역의 불량유형을 선택하세요.";

  if (qtB > 0 && rows.length < 1)
    return "불량내역에 불량유형과 불량수량을 입력하세요.";
  if (qtB < 1 && rows.length > 0) return "불량실적수량을 입력하세요.";
  if (qtB < min)
    return `불량실적수량(${qtB})은 불량유형별 최대 수량 (${min}) 이상이어야 합니다.`;
  if (qtB > max)
    return `불량실적수량(${qtB})은 불량내역 합계 (${max})을(를) 초과할 수 없습니다.`;
  if (qtW + qtB !== qt)
    return "실적수량은 양품수량+불량수량과 일치해야 합니다.";

  return null;
};

// [실적수량 합산]
// 패널 실적수량은 grid1(생산실적) 행의 합계다. 각 행은 제품 1건이며
// 양품/불량 라디오에 따라 qt_w 또는 qt_b가 1로 기록된다.
// 스캔할 때마다 패널 값에 더하는 누적 방식은 저장 후 초기화 누락이나 행삭제로
// 그리드와 어긋나므로, 항상 현재 행 전체를 다시 합산한다.
// Purpose: recompute run quantities from grid1 rows instead of accumulating.
export const sumRunQtyFromRows = (grid1Rows: any[]) => {
  const rows = Array.isArray(grid1Rows) ? grid1Rows : [];
  const qtW = rows.reduce((sum: number, row: any) => sum + toNum(row?.qt_w), 0);
  const qtB = rows.reduce((sum: number, row: any) => sum + toNum(row?.qt_b), 0);
  return { qtW, qtB, qt: qtW + qtB };
};

// sn_prd_wrk_reg_001_pop_01 결과는 고정 순서로 사용:
// [0]=header, [1]=matlCheckList, [2]=partsList, [3]=runlInspList
export const resolveWrkRegPopupTables = (data: any) => {
  return {
    matlCheckList: Array.isArray(data?.[1]) ? data[1] : [],
    partsList: Array.isArray(data?.[2]) ? data[2] : [],
    runlInspList: Array.isArray(data?.[3]) ? data[3] : [],
  };
};
